import React, { useRef, useEffect } from 'react';
import { TabType } from '../types';
import { ChevronLeft, X, Sun } from 'lucide-react';
import themeLogoIcon from '../assets/icons/homepage/theme_logo.svg';
import themeLogoIconGmc from '../assets/icons/homepage/theme_logo_gmc.svg';
import localMatchesPinIcon from '../assets/icons/homepage/local_matches_pin.svg';
import carIcon from '../assets/icons/homepage/car.svg';
import localMatchesCar from '../assets/icons/homepage/local_matches_Car.svg';
import localMatchesCarGmc from '../assets/icons/homepage/local_matches_Car_gmc.svg';
import localMatchesCarWhite from '../assets/icons/homepage/white_local_matches.svg';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { useRTL, useTranslation } from '../utils/translations';
import { uimetadata, assetUrl } from '../utils/metadata';
import { formatPrice, isCarPriceVisible } from '../utils/pricing';
import { isLocalMatchesEnabled } from '../utils/common';
import { useGetConfiguration } from '@adloid-technologies/discover-sdk';
import { useLogEvent, AnalyticsIds } from '../utils/useAnalytics';

interface HeaderProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  price: number;
  matches: number;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  onShowMatches: () => void;
  onShowSpecs?: () => void;
  modelName?: string;
  modelYear?: string | number;
  trimName?: string;
  drivetrain?: string;
  className?: string;
  hasUserInteracted?: boolean;
}

const ALL_TABS: TabType[] = ['CONFIG', 'MODEL', 'EXTERIOR', 'INTERIOR', 'OPTIONS', 'SUMMARY'];

export const Header: React.FC<HeaderProps> = ({ 
  activeTab, 
  setActiveTab, 
  price, 
  matches,
  isDarkMode,
  toggleDarkMode,
  onShowMatches,
  modelName,
  modelYear,
  trimName,
  drivetrain,
  className,
  hasUserInteracted = false,
}) => {
  const [showPopup, setShowPopup] = React.useState(false);
  const [nudgeDismissed, setNudgeDismissed] = React.useState(() => {
    try { return localStorage.getItem('localMatchesNudgeDismissed') === 'true'; } catch { return false; }
  });
  const mobileNavRef = useRef<HTMLElement>(null);
  const { isRTL } = useRTL();
  const { t, currentLanguage, setLanguage } = useTranslation();
  const { variantID } = useGetConfiguration();

  const modelId = (uimetadata as any).config?.model;
  const variantData = (uimetadata as any).models?.[modelId]?.variants?.[variantID] ?? {};
  const tabToggles = variantData.toggleTabs ?? {};

  const isGmc = (uimetadata as any).config?.brand === 'gmc';
  const moonIcon = isGmc ? themeLogoIconGmc : themeLogoIcon;
  const carIconSrc = isGmc ? localMatchesCarGmc : localMatchesCar;

  const showLogo = (uimetadata as any).featureFlags?.showLogo ?? false;
  const showLocalMatches = isLocalMatchesEnabled(uimetadata);
  const showCarPrice = isCarPriceVisible();
  const modelMeta = (uimetadata as any).models?.[modelId] ?? {};
  // headerLogo is a dedicated header-only asset, distinct from the presentation
  // transitionImage (which the loading/transition screen still uses on its own).
  // Falls back to transitionImage, then modelMeta.logo, for projects without it.
  const rawHeaderLogo = (uimetadata as any).config?.presentation?.headerLogo as string | undefined;
  const rawTransitionImage = (uimetadata as any).config?.presentation?.transitionImage as string | undefined;
  const brandLogoSrc = showLogo
    ? rawHeaderLogo
      ? `${assetUrl}${rawHeaderLogo}`
      : rawTransitionImage
        ? `${assetUrl}${rawTransitionImage}`
        : modelMeta.logo ?? null
    : null;
  // Full-color brand logos (e.g. Chevrolet) must keep their original colours in dark mode.
  // The dark-mode invert below exists only for logos whose art is solid black (e.g. CT5);
  // projects with a colour logo set headerLogoKeepColor to opt out.
  const headerLogoKeepColor = (uimetadata as any).config?.presentation?.headerLogoKeepColor ?? false;
  // The mirror-image case: Cadillac's crest (Optiq, Lyriq, Escalade IQ/IQL,
  // Vistiq, Escalade) is solid WHITE art, correctly visible as-is in dark
  // mode (hence headerLogoKeepColor:true on those, which only ever guarded
  // dark mode) but invisible against the light-mode white header with no
  // inversion at all. Projects whose logo needs the opposite polarity set
  // this to invert only in light mode, instead of dark mode.
  const headerLogoInvertInLight = (uimetadata as any).config?.presentation?.headerLogoInvertInLight ?? false;
  // Cadillac's crest reads too small at the shared default size - opt in per project.
  const headerLogoLarge = (uimetadata as any).config?.presentation?.headerLogoLarge ?? false;
  const DESKTOP_TABS = ALL_TABS.filter((tab) => tabToggles[tab]?.desktop !== false);
  const MOBILE_TABS  = ALL_TABS.filter((tab) => tabToggles[tab]?.mobile  !== false);

  const { trackEvent } = useLogEvent();

  React.useEffect(() => {
    if (!showLocalMatches || nudgeDismissed || !hasUserInteracted) return;
    const showTimer = setTimeout(() => {
      setShowPopup(true);
      trackEvent(AnalyticsIds.matchingNudgeShown, {
        primaryTypeName: 'matchingVehicles',
        secondaryTypeName: String(matches),
      });
    }, 10000);
    return () => clearTimeout(showTimer);
  }, [hasUserInteracted, nudgeDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismissPopup = (dismissalType: 'dismiss' | 'accepted' = 'dismiss') => {
    trackEvent(AnalyticsIds.matchingNudgeInteract, {
      primaryTypeName: 'matchingVehicles',
      secondaryTypeName: dismissalType,
    });
    setShowPopup(false);
    setNudgeDismissed(true);
    try { localStorage.setItem('localMatchesNudgeDismissed', 'false'); } catch {}
  };

  useEffect(() => {
    const nav = mobileNavRef.current;
    if (!nav) return;
    const activeBtn = nav.querySelector<HTMLElement>(`[data-tab="${activeTab}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab]);

  return (
    <header dir={isRTL ? 'rtl' : 'ltr'} className={cn(
      "flex flex-col border-b transition-colors duration-300 z-61",
      isDarkMode ? "bg-[#0a0a0a] border-white/5" : "bg-white border-gray-100",
      className,
    )}>
      {/* Brand Logo Strip — Desktop only */}
      {brandLogoSrc && (
        <div className="flex items-end justify-center h-[35px] min-[1165px]:h-[40px] overflow-visible">
          <img
            src={brandLogoSrc}
            alt="Brand Logo"
            className={cn('object-contain flex-shrink-0', headerLogoLarge ? 'w-[140px] h-[37px] min-[1165px]:w-[190px] min-[1165px]:h-[50px]' : 'w-[72px] h-[19px] min-[1165px]:w-[98.5px] min-[1165px]:h-[26px]')}
            // CT5's headerLogo art is solid black (opaque pixels, transparent
            // background) — invisible against the dark-mode near-black header, so invert
            // it to white there. Untouched in light mode, and skipped entirely for
            // full-color logos (headerLogoKeepColor) like Chevrolet's, which must stay as-is.
            // Cadillac's white crest is the mirror case: invisible against the
            // light-mode white header instead, so headerLogoInvertInLight flips it
            // to black there and leaves dark mode alone.
            style={
              isDarkMode
                ? headerLogoKeepColor ? undefined : { filter: 'invert(1)' }
                : headerLogoInvertInLight ? { filter: 'invert(1)' } : undefined
            }
          />
        </div>
      )}
      {/* Main Header */}
      <div className="h-[44px] md:h-[75px] flex items-center justify-between px-4 md:px-6 relative">
        {/* Left: Model Info — capped so a long trim name truncates instead of running
            under the centered nav. Brand + drivetrain badge never shrink.
            The nav's own center shifts left at the narrower desktop widths
            (see its min-[1165px]:max-[1249px]/min-[1250px]:max-[1309px]
            overrides below) - a single calc(50% - 360px) cap sized for the
            nav's widest (1475px+, left-1/2) position left this box free to
            grow right under the nav at those in-between widths. Mirror the
            same breakpoint tiers here so the cap always matches where the
            nav actually sits. */}
        <div className="flex items-baseline gap-[6px] min-w-0 max-w-[52%] min-[1165px]:max-w-[calc(46.5%-360px)] min-[1165px]:max-[1249px]:max-w-[calc(42.5%-360px)] min-[1250px]:max-[1309px]:max-w-[calc(45%-360px)] min-[1475px]:max-w-[calc(50%-360px)]">
          <h1 className={cn(
            'font-sans text-[14px] md:text-[24px] font-bold uppercase leading-none flex-shrink-0',
            isDarkMode ? "text-white" : "text-black"
          )}>
            {modelYear ? `${modelYear} ` : ''}{modelName ?? 'Configurator'}
          </h1>
          {trimName && (
            <span className={cn(
              'font-sans text-[14px] md:text-[15px] font-bold uppercase leading-none truncate min-w-0',
              isDarkMode ? "text-white/40" : "text-[#6e6e6e]"
            )}>{trimName}</span>
          )}
          {drivetrain && (
            <span className={cn(
              'font-sans text-[9px] md:text-[12px] font-bold uppercase leading-none px-[5px] py-[2px] rounded flex-shrink-0',
              isDarkMode ? 'text-white/60 bg-white/10' : 'text-[#666] bg-gray-100'
            )}>{drivetrain}</span>
          )}
        </div>

        {/* Center: Navigation (Desktop Only) */}
        <nav className="hidden min-[1165px]:flex items-center absolute -translate-x-1/2 h-full top-0 [gap:calc(var(--spacing)*4)] [left:calc(1/2*93%)] min-[1165px]:max-[1249px]:[gap:calc(var(--spacing)*1)] min-[1165px]:max-[1249px]:[left:calc(1/2*85%)] min-[1250px]:max-[1309px]:[gap:calc(var(--spacing)*2)] min-[1250px]:max-[1309px]:[left:calc(1/2*90%)] min-[1475px]:gap-8 min-[1475px]:left-1/2">
          {DESKTOP_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                trackEvent(AnalyticsIds.navigationInteract, {
                  parent: 'tab',
                  primaryTypeName: tab,
                  secondaryTypeName: activeTab,
                });
                setActiveTab(tab);
              }}
              className={cn(
                'font-sans h-full px-1 text-[18px] uppercase transition-all flex flex-col items-center justify-center gap-[4px] font-bold',
                activeTab === tab
                  ? (isDarkMode ? "text-white" : "text-black")
                  : (isDarkMode ? "text-white/40 hover:text-white" : "text-[#666666] hover:text-black")
              )}
            >
              <span>{t(`staticTranslations.tabs.${tab.toLowerCase()}`, tab)}</span>
              <div className={cn(
                "h-[1.5px] w-full rounded-full transition-all",
                activeTab === tab ? "bg-[var(--brand-accent)]" : "bg-transparent"
              )} />
            </button>
          ))}
        </nav>

        {/* Right: Actions & Stats */}
        <div className="flex items-center gap-1 md:gap-6">

          {/* Dark/Light mode toggle — Figma: circular, brand-accent-bordered, moon icon */}
          <button
            onClick={toggleDarkMode}
            className={cn(
              'rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0',
              'size-[27px] md:size-[34px]',
              isDarkMode
                ? 'bg-[#1a1a1a] border border-white/20 hover:border-white/40'
                : 'bg-white border border-[var(--brand-accent)] hover:bg-[var(--brand-accent-light)]',
            )}
          >
            {isDarkMode ? (
              <Sun className="w-[15px] h-[15px] md:w-[18px] md:h-[18px] text-white" strokeWidth={1.5} fill="none" />
            ) : (
              <img src={moonIcon} alt="Toggle theme" className="w-[15px] h-[15px] md:w-[18px] md:h-[18px] object-contain" />
            )}
          </button>

          {/* Mobile: Local Matches — pill button */}
          {showLocalMatches && (
            <div
              className={cn(
                    'flex md:hidden items-center gap-[8px] cursor-pointer h-[32px] px-[12px] rounded-[var(--brand-pill-radius)] border',
                isDarkMode ? 'border-white/20' : 'border-[var(--brand-accent)]',
              )}
              onClick={() => {
              trackEvent(AnalyticsIds.matchingVehiclesInteract, {
                parent: 'header',
                primaryTypeName: String(matches),
              });
              onShowMatches();
            }}
            >
              <div className={cn('font-sans text-[10px] uppercase leading-tight font-medium', isRTL ? 'text-left' : 'text-right', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
                {t('staticTranslations.header.localMatches', 'Local\nMatches').split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              <div className="flex items-center gap-[3px]">
                {isDarkMode ? (
                  <img src={localMatchesCarWhite} alt="car" className="w-[20px] h-[10px] object-contain" />
                ) : (
                  <img src={carIconSrc} alt="car" className="w-[20px] h-[10px] object-contain" />
                )}
                <span className={cn('font-sans text-[15.75px] font-bold leading-none', isDarkMode ? 'text-white' : 'text-[#333334]')}>{matches}</span>
              </div>
            </div>
          )}

          <div className="hidden md:flex items-center gap-[24px]">
            {/* Price Display */}
            {showCarPrice && (
            <div className="flex flex-col items-end justify-center gap-[2px]">
              <span className={cn('font-sans text-[12px] uppercase leading-none whitespace-nowrap font-medium', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
                {t('staticTranslations.common.startingPrice', 'Starting Price')}
              </span>
              <span className={cn('font-sans text-[24px] leading-none whitespace-nowrap font-bold', isDarkMode ? 'text-white' : 'text-black')}>
                {formatPrice(price)}
              </span>
              <span className={cn('font-sans text-[12px] uppercase leading-none whitespace-nowrap font-medium', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
                {t('staticTranslations.common.msrp', 'MSRP')}
              </span>
            </div>
            )}

            {/* Matching Vehicles box — Figma: bordered card w/ car icon + count */}
            {showLocalMatches && (
            <div className="relative">
              <button
                onClick={() => {
              trackEvent(AnalyticsIds.matchingVehiclesInteract, {
                parent: 'header',
                primaryTypeName: String(matches),
              });
              onShowMatches();
            }}
                className={cn(
                  // Width grows with the label instead of being fixed at 149px:
                  // uppercased "MATCHING VEHICLES" is ~147px and used to spill
                  // past the border. 149px stays the floor, so shorter labels
                  // (Arabic, other projects) keep the original card size.
                  'flex flex-col justify-center gap-[9px] h-[63px] min-w-[149px] shrink-0 px-[16px] rounded-[var(--brand-button-radius)] border transition-colors cursor-pointer',
                  isRTL ? 'items-start' : 'items-end',
                  isDarkMode ? 'border-white/20 hover:border-white/40' : 'border-[var(--brand-accent)]',
                )}
              >
                <span className={cn('font-sans text-[12px] uppercase leading-none whitespace-nowrap font-medium', isRTL ? 'text-left' : 'text-right', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
                  {t('staticTranslations.header.matchingVehicles', 'Matching vehicles')}
                </span>
                <div className="flex items-center gap-[11px]">
                  {isDarkMode ? (
                    <img
                      src={localMatchesCarWhite}
                      alt="vehicle"
                      className="w-[34px] h-[14px] object-contain"
                    />
                  ) : (
                    <img src={carIconSrc} alt="vehicle" className="w-[34px] h-[14px] object-contain" />
                  )}
                  <span className={cn('font-sans text-[24px] leading-none whitespace-nowrap font-bold', isDarkMode ? 'text-white' : 'text-black')}>
                    {matches}
                  </span>
                </div>
              </button>

              {/* Nudge popup — Figma: always blue, triangle caret, yellow badge button */}
              <AnimatePresence>
                {showPopup && (
                  <>
                    {/* Desktop nudge */}
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={cn(
                        'hidden md:block absolute top-full z-[9999] w-[247px]',
                        isRTL ? 'left-0' : 'right-0',
                      )}
                    >
                      {/* Upward-pointing triangle caret */}
                      <div className={cn('flex', isRTL ? 'justify-start pl-[24px]' : 'justify-end pr-[24px]')}>
                        <div style={{
                          width: 0, height: 0,
                          borderLeft: '9px solid transparent',
                          borderRight: '9px solid transparent',
                          borderBottom: '10px solid var(--brand-accent)',
                        }} />
                      </div>
                      {/* Popup body */}
                      <div className="bg-[var(--brand-accent)] rounded-[var(--brand-card-radius-sm)] overflow-hidden shadow-2xl" style={{ width: '247px' }}>
                        {/* Header row */}
                        <div className="flex items-start justify-between px-[14px] pt-[17px] pb-[10px]">
                          <span className='font-sans text-[16px] font-bold text-white leading-none'>
                            {t('staticTranslations.header.configurationsFound', 'Configurations Found')}
                          </span>
                          <button onClick={handleDismissPopup} className="text-white/70 hover:text-white transition-colors flex-shrink-0 mt-[-1px] cursor-pointer">
                            <X className="w-[12.6px] h-[12.6px]" strokeWidth={2.5} />
                          </button>
                        </div>
                        {/* Body text */}
                        <p className='font-sans text-[12px] text-white leading-[1.3] px-[14px] pb-[14px]'>
                          {t('staticTranslations.localMatchesNudge.message', 'We Found a few local matches similar to your current configuration. Click to view Matches')}
                        </p>
                        {/* CTA row */}
                        <div className="mx-[14px] mb-[16px]">
                          <button
                            onClick={() => {
                              handleDismissPopup('accepted');
                              trackEvent(AnalyticsIds.matchingVehiclesInteract, {
                                parent: 'nudge',
                                primaryTypeName: String(matches),
                              });
                              onShowMatches();
                            }}
                            className={cn(
                              'w-full bg-[#edf3ff] border border-white rounded-[var(--brand-button-radius)] flex items-center transition-colors hover:bg-white cursor-pointer',
                              isRTL ? 'flex-row-reverse' : '',
                            )}
                            style={{ height: '40px' }}
                          >
                            {/* Yellow badge */}
                            <div className="bg-[#f2bd17] rounded-[var(--brand-card-radius-sm)] flex items-center justify-center flex-shrink-0" style={{ width: '43px', height: '29px', margin: isRTL ? '0 0 0 auto' : '' }}>
                              <span className='font-sans text-[12px] font-semibold text-black leading-none'>
                                {matches}+
                              </span>
                            </div>
                            <span className='font-sans text-[12px] font-bold text-black leading-none px-[10px]'>
                              {t('staticTranslations.topBar.matchingVehiclesFound', 'Matching Vehicles Found')}
                            </span>
                          </button>
                        </div>
                      </div>
                    </motion.div>

                    {/* Mobile nudge — fixed below header */}
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={cn(
                        'md:hidden fixed top-[95px] z-[9999] w-[224px]',
                        isRTL ? 'left-3' : 'right-3',
                      )}
                    >
                      {/* Upward caret */}
                      <div className={cn('flex', isRTL ? 'justify-start pl-[20px]' : 'justify-end pr-[20px]')}>
                        <div style={{
                          width: 0, height: 0,
                          borderLeft: '7px solid transparent',
                          borderRight: '7px solid transparent',
                          borderBottom: '8px solid var(--brand-accent)',
                        }} />
                      </div>
                      <div className="bg-[var(--brand-accent)] rounded-[var(--brand-card-radius-sm)] overflow-hidden shadow-2xl">
                        <div className="flex items-start justify-between px-[12px] pt-[14px] pb-[8px]">
                          <span className='font-sans text-[14px] font-bold text-white leading-none'>
                            {t('staticTranslations.header.configurationsFound', 'Configurations Found')}
                          </span>
                          <button onClick={handleDismissPopup} className="text-white/70 hover:text-white transition-colors flex-shrink-0">
                            <X className="w-[11px] h-[11px]" strokeWidth={2.5} />
                          </button>
                        </div>
                        <p className='font-sans text-[10px] text-white leading-[1.3] px-[12px] pb-[12px]'>
                          {t('staticTranslations.localMatchesNudge.message', 'We Found a few local matches similar to your current configuration. Click to view Matches')}
                        </p>
                        <div className="mx-[12px] mb-[14px]">
                          <button
                            onClick={() => {
                              handleDismissPopup('accepted');
                              trackEvent(AnalyticsIds.matchingVehiclesInteract, {
                                parent: 'nudge',
                                primaryTypeName: String(matches),
                              });
                              onShowMatches();
                            }}
                            className={cn(
                              'w-full bg-[#edf3ff] border border-white rounded-[var(--brand-button-radius-sm)] flex items-center transition-colors hover:bg-white cursor-pointer',
                              isRTL ? 'flex-row-reverse' : '',
                            )}
                            style={{ height: '34px' }}
                          >
                            <div className="bg-[#f2bd17] rounded-[var(--brand-card-radius-sm)] flex items-center justify-center flex-shrink-0" style={{ width: '38px', height: '26px' }}>
                              <span className='font-sans text-[10px] font-semibold text-black leading-none'>
                                {matches}+
                              </span>
                            </div>
                            <span className='font-sans text-[10px] font-bold text-black leading-none px-[8px]'>
                              {t('staticTranslations.topBar.matchingVehiclesFound', 'Matching Vehicles Found')}
                            </span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Tabs */}
      <nav ref={mobileNavRef} className="min-[1165px]:hidden flex items-center overflow-x-auto no-scrollbar px-4 h-11 gap-[23px]">
        {MOBILE_TABS.map((tab) => (
          <button
            key={tab}
            data-tab={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'font-sans h-full whitespace-nowrap text-[12px] uppercase transition-all flex flex-col items-center justify-center gap-[4px] font-bold',
              activeTab === tab
                ? (isDarkMode ? 'text-white' : 'text-[#333334]')
                : (isDarkMode ? 'text-white/40' : 'text-[#666666]')
            )}
          >
            <span>{t(`staticTranslations.tabs.${tab.toLowerCase()}`, tab)}</span>
            <div className={cn(
              'h-[1.5px] w-full rounded-full transition-all',
              activeTab === tab ? 'bg-[var(--brand-accent)]' : 'bg-transparent'
            )} />
          </button>
        ))}
      </nav>
    </header>
  );
};
